/**
 * Profile Screen - MOBILE ONLY
 * 
 * Edit user profile information.
 * Stacked flow - back button, no bottom tabs.
 */

import React, { useState, useCallback } from 'react';
import { View, Text, ScrollView, StyleSheet, TextInput, Pressable, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import { useAuthStore } from '../../../store/authStore';
import { useProfile } from '../../../hooks/useProfile';
import { color, text, space, radius, touch, icon, layout, border } from '../../../shared/tokens/mobile';
import { ScreenContainer, SectionHeader, ListCard } from '../../../components/v2/mobile';

export default function ProfileScreen() {
  const router = useRouter();
  const user = useAuthStore((state) => state.user);
  const { updateProfile, isLoading, error } = useProfile();
  
  const [displayName, setDisplayName] = useState(user?.display_name || '');
  const [hasChanges, setHasChanges] = useState(false);
  const email = user?.email || '';

  const handleBack = useCallback(() => {
    router.back();
  }, [router]);

  const handleDisplayNameChange = useCallback((value: string) => {
    setDisplayName(value);
    setHasChanges(value !== (user?.display_name || ''));
  }, [user?.display_name]);

  const handleSave = useCallback(async () => {
    if (!hasChanges || isLoading) return;
    
    const success = await updateProfile({ display_name: displayName.trim() });
    if (success) {
      router.back();
    }
  }, [hasChanges, isLoading, displayName, updateProfile, router]);

  // Format member since date
  const memberSince = user?.created_at 
    ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : 'December 2024';

  return (
    <ScreenContainer>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* HEADER */}
        <View style={styles.header}>
          <Pressable onPress={handleBack} style={styles.backButton} accessibilityRole="button" accessibilityLabel="Go back">
            <Ionicons name="chevron-back" size={icon.lg} color={color.textPrimary} />
          </Pressable>
          <Text style={styles.title}>Profile</Text>
          <Pressable 
            onPress={handleSave} 
            style={[styles.saveButton, (!hasChanges || isLoading) && styles.saveButtonDisabled]}
            disabled={!hasChanges || isLoading}
            accessibilityRole="button"
            accessibilityLabel="Save changes"
          >
            {isLoading ? (
              <ActivityIndicator size="small" color={color.primary} />
            ) : (
              <Text style={[styles.saveButtonText, !hasChanges && styles.saveButtonTextDisabled]}>
                Save
              </Text>
            )}
          </Pressable>
        </View>

        {/* ERROR MESSAGE */}
        {error && (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>{error}</Text>
          </View>
        )}

        {/* AVATAR */}
        <View style={styles.avatarSection}>
          <View style={styles.avatar}>
            <Text style={styles.avatarInitial}>
              {(displayName || email).charAt(0).toUpperCase()}
            </Text>
          </View>
          <Pressable style={styles.changePhotoButton} accessibilityRole="button" accessibilityLabel="Change photo">
            <Text style={styles.changePhotoText}>Change Photo</Text>
          </Pressable>
        </View>

        {/* PROFILE FIELDS */}
        <View style={styles.section}>
          <SectionHeader label="Profile Information" />
          <ListCard>
            <View style={styles.inputRow}>
              <Text style={styles.inputLabel}>Display Name</Text>
              <TextInput
                style={styles.input}
                value={displayName}
                onChangeText={handleDisplayNameChange}
                placeholder="Enter your name"
                placeholderTextColor={color.textMuted}
                autoCapitalize="words"
                autoCorrect={false}
                accessibilityLabel="Display name input"
              />
            </View>
            <View style={[styles.inputRow, styles.inputRowLast]}>
              <Text style={styles.inputLabel}>Email</Text>
              <Text style={styles.inputValue}>{email}</Text>
              <Text style={styles.inputHint}>Contact support to change email</Text>
            </View>
          </ListCard>
        </View>

        {/* ACCOUNT INFO */}
        <View style={styles.section}>
          <SectionHeader label="Account" />
          <ListCard>
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Role</Text>
              <Text style={styles.infoValue}>{user?.role === 'admin' ? 'Administrator' : 'User'}</Text>
            </View>
            <View style={[styles.infoRow, styles.infoRowLast]}>
              <Text style={styles.infoLabel}>Member since</Text>
              <Text style={styles.infoValue}>{memberSince}</Text>
            </View>
          </ListCard>
        </View>
      </ScrollView>
    </ScreenContainer>
  );
}

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: layout.scrollBottomPadding,
  },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: space.sm,
    paddingVertical: space.sm,
  },
  backButton: {
    width: touch.minimum,
    height: touch.minimum,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    flex: 1,
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textPrimary,
    textAlign: 'center',
  },
  saveButton: {
    paddingHorizontal: space.md,
    paddingVertical: space.sm,
    minWidth: touch.minimum,
    alignItems: 'center',
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.primary,
  },
  saveButtonTextDisabled: {
    color: color.textMuted,
  },

  // Error
  errorContainer: {
    marginHorizontal: space.md,
    marginBottom: space.md,
    padding: space.md,
    backgroundColor: color.errorLight,
    borderRadius: radius.md,
  },
  errorText: {
    fontSize: text.caption.fontSize,
    fontWeight: text.caption.fontWeight,
    color: color.error,
    textAlign: 'center',
  },

  // Avatar
  avatarSection: {
    alignItems: 'center',
    paddingVertical: space.xl,
  },
  avatar: {
    width: 80,
    height: 80,
    borderRadius: radius.full,
    backgroundColor: color.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarInitial: {
    fontSize: 32,
    fontWeight: text.pageTitle.fontWeight,
    color: color.textInverse,
  },
  changePhotoButton: {
    marginTop: space.md,
    paddingVertical: space.sm,
    paddingHorizontal: space.md,
  },
  changePhotoText: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.primary,
  },

  // Sections
  section: {
    paddingHorizontal: space.md,
    marginBottom: space.lg,
  },

  // Input rows
  inputRow: {
    paddingVertical: space.md,
    paddingHorizontal: space.md,
    borderBottomWidth: border.width.thin,
    borderBottomColor: color.divider,
  },
  inputRowLast: {
    borderBottomWidth: 0,
  },
  inputLabel: {
    fontSize: text.caption.fontSize,
    fontWeight: text.caption.fontWeight,
    color: color.textSecondary,
    marginBottom: space.xs,
  },
  input: {
    fontSize: text.body.fontSize,
    fontWeight: text.body.fontWeight,
    color: color.textPrimary,
    padding: 0,
  },
  inputValue: {
    fontSize: text.body.fontSize,
    fontWeight: text.body.fontWeight,
    color: color.textPrimary,
  },
  inputHint: {
    fontSize: text.caption.fontSize,
    fontWeight: text.caption.fontWeight,
    color: color.textMuted,
    marginTop: space.xs,
  },

  // Info rows
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: space.md,
    paddingHorizontal: space.md,
    borderBottomWidth: border.width.thin,
    borderBottomColor: color.divider,
  },
  infoRowLast: {
    borderBottomWidth: 0,
  },
  infoLabel: {
    fontSize: text.body.fontSize,
    fontWeight: text.body.fontWeight,
    color: color.textSecondary,
  },
  infoValue: {
    fontSize: text.body.fontSize,
    fontWeight: text.body.fontWeight,
    color: color.textPrimary,
  },
});
